import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { useCompanyRevenue } from "../hooks/useCompanyRevenue";

import { getChartColors } from "../constants/chartColors";
import { formatNumber } from "../utils/formatNumber";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import GrowthMetricsDisplay from "./shared/GrowthMetricsDisplay";
import ChartTooltip from "./shared/ChartTooltip";

interface RevenueChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const RevenueChart: React.FC<RevenueChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode === "dark");

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);
  const [revenueView, setRevenueView] = useState("reported");
  const isTtm = revenueView === "ttm";

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const displayLimit =
    isTtm && effectiveViewMode === "annual"
      ? effectiveLimit * 4
      : effectiveLimit;
  const period = isTtm ? "ttm" : getApiPeriodParam(effectiveViewMode);
  const reportedMaxLimit =
    effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const maxLimit = isTtm
    ? Math.max(MAX_QUARTERLY_LIMIT, displayLimit + 4)
    : reportedMaxLimit;
  const { data, isLoading, error } = useCompanyRevenue(
    ticker,
    maxLimit,
    period,
  );

  const currency = data?.points[0]?.currency;
  const currencySymbol =
    currency === "USD" ? "$" : currency ? `${currency} ` : "";
  const chartData = useMemo(() => {
    const points = data?.points ?? [];
    const latestBasis = points[0]?.basis;
    const visible = safeSlice([...points].reverse(), displayLimit);
    if (
      !visible.some(
        (point) => point.value !== null && point.basis === latestBasis,
      )
    )
      return [];
    return visible.map((point) => ({
      date: point.date,
      period: `${point.period} ${point.fiscalYear}`,
      revenue:
        point.value !== null &&
        point.currency === currency &&
        point.basis === latestBasis
          ? point.value / 1e6
          : null,
      yoyGrowth: point.value === null || point.currency !== currency || point.basis !== latestBasis || point.growth === null ? null : point.growth * 100,
      reason: point.reason,
    }));
  }, [data, currency, displayLimit]);
  const latestYoy = data?.points[0]?.growth;
  const growthMetrics =
    latestYoy !== null && latestYoy !== undefined
      ? [{ label: "Year-over-year growth", value: latestYoy * 100 }]
      : [];

  const reportedLabel =
    effectiveViewMode === "quarterly" ? "Quarterly" : "Annual";
  const chartViewMode = isTtm ? "quarterly" : effectiveViewMode;
  const revenueLabel = isTtm ? "TTM revenue" : "Revenue";
  const noDataTitle = "Revenue unavailable";
  const noDataDescription =
    "Comparable revenue is not available for these periods.";

  return (
    <GenericFinancialBarChart
      title="Revenue Growth"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      headerToggleOptions={[
        { value: "reported", label: reportedLabel },
        { value: "ttm", label: "TTM" },
      ]}
      headerToggleValue={revenueView}
      onHeaderToggleChange={setRevenueView}
      keepHeaderWhenEmpty
      noDataTitle={noDataTitle}
      noDataDescription={noDataDescription}
      chartData={chartData}
      viewMode={chartViewMode}
      barKeys={["revenue"]}
      colors={[colors.positive]}
      yAxisLegend={revenueLabel}
      yAxisFormat={(value) =>
        `${currencySymbol}${formatNumber(value * 1000000)}`
      }
      tooltipContent={({ indexValue, value, color, data: point }) => {
        const header = isTtm
          ? `TTM ended ${dayjs(point.date).format("MMM D, YYYY")}`
          : String(indexValue);
        const rows = [
          {
            label: revenueLabel,
            value: point.revenue === null ? "Unavailable" : `${currencySymbol}${formatNumber((value as number) * 1000000, 2)}`,
          },
        ];
        if (point.yoyGrowth !== null && point.yoyGrowth !== undefined) {
          rows.push({
            label: "Year-over-year growth",
            value: `${point.yoyGrowth.toFixed(1)}%`,
          });
        }
        return <ChartTooltip header={header} headerColor={color} rows={rows} />;
      }}
      growthMetrics={<GrowthMetricsDisplay metrics={growthMetrics} />}
    />
  );
};

export default RevenueChart;
